import { useState, useEffect } from 'react';

interface AdminUser {
  id: string;
  email: string;
  username: string;
  last_login: string;
}

interface AuthState {
  admin: AdminUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const TOKEN_KEY = 'ff_admin_token';

export const useAdminAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    admin: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Verificar sessão ao carregar
  useEffect(() => {
    validateSession();
  }, []);

  const validateSession = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    
    if (!token) {
      setAuthState({
        admin: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/validate-admin-session`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        }
      );

      const data = await response.json();

      if (data.valid && data.admin) {
        setAuthState({
          admin: data.admin,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        localStorage.removeItem(TOKEN_KEY);
        setAuthState({
          admin: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    } catch (error) {
      console.error('Erro ao validar sessão:', error);
      localStorage.removeItem(TOKEN_KEY);
      setAuthState({
        admin: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/secure-admin-login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao fazer login');
      }

      if (data.success && data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        setAuthState({
          admin: data.admin,
          token: data.token,
          isAuthenticated: true,
          isLoading: false,
        });
        return { success: true };
      }

      throw new Error('Resposta inválida do servidor');
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthState({
      admin: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  return {
    admin: authState.admin,
    token: authState.token,
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    login,
    logout,
    validateSession,
  };
};
